// Utilities for MAT AG Grid table: sorting and basic URL param reading

import snakeCase from 'just-snake-case';

// Converts primary AG Grid sort to API order string using snake_case and '-' for desc
export const buildOrdering = (sortModel: any[] | undefined): string | undefined => {
  if (!sortModel || !sortModel.length) return undefined;
  const primary = sortModel[0];
  const field = primary?.colId as string;
  if (!field) return undefined;
  const snake = snakeCase(field);
  const dir = primary.sort === 'desc' ? '-' : '';
  return `${dir}${snake}`;
};

// Reads a field's value from URL using aliases; validates DataSourceStatus against allowed options
export const readUrlParam = (
  field: string,
  sp: URLSearchParams,
  statusOptions: readonly string[]
): string | null => {
  const paramValue = sp.get(field);
  if (paramValue != null && paramValue !== '') {
    if (field === 'DataSourceStatus') {
      if (statusOptions.includes(paramValue)) return paramValue;
      return null;
    }
    return paramValue;
  }
  return null;
};

// Build AG Grid column definitions from the first data row, marking filterable fields
export const buildColumnDefsFromRow = (
  firstRow: Record<string, any>,
  filterableKeys: ReadonlySet<string>
): any[] => {
  const keys = Object.keys(firstRow);

  // Filter out internal columns (those starting with __)
  const visibleKeys = keys.filter((k) => !k.startsWith('__'));

  return visibleKeys.map((k) => {
    const base: any = {
      headerName: k,
      field: k,
      sortable: true,
    };

    if (filterableKeys.has(k)) {
      base.filter = 'agTextColumnFilter';
    } else {
      base.filter = false;
    }

    return base;
  });
};

// Apply client-side filtering to discovered data based on URL filter params
export const applyClientSideFilters = (
  rows: any[],
  urlParams: URLSearchParams,
  filterableKeys: ReadonlySet<string>
): any[] => {
  if (!rows || rows.length === 0) return rows;

  return rows.filter((row) => {
    for (const key of filterableKeys) {
      const filterValue = urlParams.get(key);
      if (!filterValue) continue;

      const cellValue = row[key];

      // An active column filter must not match a row with no value for that column.
      if (cellValue == null || cellValue === '') {
        return false;
      }

      // Convert to string for comparison (case-insensitive contains)
      const cellStr = String(cellValue).toLowerCase();
      const filterStr = filterValue.toLowerCase();

      // AG Grid default text filter is "contains"
      if (!cellStr.includes(filterStr)) {
        return false; // Row doesn't match this filter
      }
    }

    return true; // Row matches all filters
  });
};
